import React from "react";
import {
  Box,
  Flex,
  Grid,
  GridItem,
  HStack,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";

type FileRow = { row: number; side: "BUY" | "SELL"; qty: string };

type PairedTrade = {
  label: string;
  open: FileRow;
  close: FileRow;
};

const FILE_ROWS: FileRow[] = [
  { row: 1, side: "BUY", qty: "1.0" },
  { row: 2, side: "BUY", qty: "0.5" },
  { row: 3, side: "SELL", qty: "1.0" },
  { row: 4, side: "SELL", qty: "0.5" },
];

const PAIRED_TRADES: PairedTrade[] = [
  {
    label: "Trade 1",
    open: FILE_ROWS[0]!,
    close: FILE_ROWS[2]!,
  },
  {
    label: "Trade 2",
    open: FILE_ROWS[1]!,
    close: FILE_ROWS[3]!,
  },
];

const FifoMatchingIllustration = (): React.ReactElement => {
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headingColor = useColorModeValue("gray.500", "gray.400");
  const bodyColor = useColorModeValue("gray.800", "gray.100");
  const rowBg = useColorModeValue("gray.50", "gray.700");
  const tradeBg = useColorModeValue("blue.50", "gray.700");
  const buyColor = useColorModeValue("green.700", "green.300");
  const sellColor = useColorModeValue("red.700", "red.300");
  const tradeLabelColor = useColorModeValue("blue.700", "blue.200");
  const arrowColor = useColorModeValue("gray.500", "gray.400");

  const sideColor = (side: "BUY" | "SELL") => (side === "BUY" ? buyColor : sellColor);

  return (
    <Box
      role="img"
      aria-label="Example showing how rows in a CSV file are paired into completed trades in file order"
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      p={4}
      bg={cardBg}
      color={bodyColor}
      shadow="sm"
    >
      <Text fontSize="sm" color={bodyColor} mb={3}>
        Example: Tessera reads your rows top to bottom and pairs the first buy with the
        first sell, then the next buy with the next sell.
      </Text>

      <Grid templateColumns={{ base: "1fr", md: "1fr auto 1fr" }} gap={3} alignItems="stretch">
        <GridItem>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            textTransform="uppercase"
            letterSpacing="wider"
            color={headingColor}
            mb={2}
          >
            Rows in your file
          </Text>
          <VStack align="stretch" spacing={1.5}>
            {FILE_ROWS.map((fill) => (
              <Flex
                key={fill.row}
                align="center"
                gap={2}
                px={2.5}
                py={1.5}
                bg={rowBg}
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="md"
              >
                <Text fontSize="xs" fontWeight="bold" minW="16px" color={headingColor}>
                  {fill.row}
                </Text>
                <Text fontSize="sm" fontWeight="semibold" color={sideColor(fill.side)}>
                  {fill.side}
                </Text>
                <Text fontSize="sm" color={bodyColor}>
                  {fill.qty}
                </Text>
              </Flex>
            ))}
          </VStack>
        </GridItem>

        <GridItem display={{ base: "none", md: "flex" }} alignItems="center" justifyContent="center">
          <Text fontSize="2xl" color={arrowColor} aria-hidden>
            →
          </Text>
        </GridItem>

        <GridItem>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            textTransform="uppercase"
            letterSpacing="wider"
            color={headingColor}
            mb={2}
          >
            Trades Tessera creates
          </Text>
          <VStack align="stretch" spacing={2}>
            {PAIRED_TRADES.map((trade) => (
              <Box
                key={trade.label}
                px={2.5}
                py={2}
                bg={tradeBg}
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="md"
              >
                <Text fontSize="xs" fontWeight="bold" color={tradeLabelColor} mb={1.5}>
                  {trade.label}
                </Text>
                <VStack align="stretch" spacing={1}>
                  <HStack spacing={2} fontSize="sm">
                    <Text color={headingColor} minW="42px">
                      Open
                    </Text>
                    <Text fontWeight="semibold" color={sideColor(trade.open.side)}>
                      {trade.open.side}
                    </Text>
                    <Text color={bodyColor}>{trade.open.qty}</Text>
                    <Text fontSize="xs" color={headingColor}>
                      (row {trade.open.row})
                    </Text>
                  </HStack>
                  <HStack spacing={2} fontSize="sm">
                    <Text color={headingColor} minW="42px">
                      Close
                    </Text>
                    <Text fontWeight="semibold" color={sideColor(trade.close.side)}>
                      {trade.close.side}
                    </Text>
                    <Text color={bodyColor}>{trade.close.qty}</Text>
                    <Text fontSize="xs" color={headingColor}>
                      (row {trade.close.row})
                    </Text>
                  </HStack>
                </VStack>
              </Box>
            ))}
          </VStack>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default FifoMatchingIllustration;
